/**
 * Instructor Image Configuration
 * 
 * Central configuration for all Lior Murphy images used throughout the trainee platform.
 * Images are stored in Firebase Storage at: landing/pose/
 * 
 * Total: 29 images mapped to various uses across the platform
 */

import { ClassName } from '@/types/class';

// Base Firebase Storage path
const POSE_BASE = 'landing/pose/';

// Base Firebase Storage URL structure
const STORAGE_BASE = process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET 
  ? `https://firebasestorage.googleapis.com/v0/b/${process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET}/o/`
  : '';

/**
 * Helper to create Firebase Storage URL
 * @param filename - Filename in landing/pose/ (e.g., 'handstand.jpg')
 * @returns Full Firebase Storage URL
 */
export const getStorageUrl = (filename: string): string => {
  if (!STORAGE_BASE) {
    console.warn('Firebase Storage not configured');
    // Use an existing logo as fallback instead of non-existent placeholder
    return '/LIOR_B_1024.png';
  }
  const fullPath = `${POSE_BASE}${filename}`;
  return `${STORAGE_BASE}${encodeURIComponent(fullPath)}?alt=media`;
};

/**
 * =================================================================================
 * RAW IMAGE MAP - All Available Images
 * Use this reference when adding new features or layouts
 * =================================================================================
 */
export const allAvailableImages = {
  // HANDSTAND CATEGORY (6 images)
  handstands: {
    'handstand.jpg': 'Main handstand pose',
    'practice-handstands.jpg': 'Handstand practice session',
    'moveom-press.jpg': 'Press to handstand movement',
    'pike-press.jpg': 'Pike press handstand entry',
    'press.jpg': 'Press handstand',
    'press-touch.jpg': 'Press with touch assistance',
  },

  // SPLIT CATEGORY (4 images)
  splits: {
    'hanging-split.jpg': 'Hanging split (front)',
    'practice-side-split.jpg': 'Side split practice',
    'squat-split.jpg': 'Squat split (middle)',
    'squatsplit_portrait.png': 'Squat split portrait format',
  },

  // BACK FLEXIBILITY / BRIDGE CATEGORY (10 images)
  backFlexibility: {
    'alien.jpg': 'Alien bridge pose variant',
    'alien2.jpg': 'Alien bridge pose variant 2',
    'bridge.PNG': 'Standard bridge pose',
    'practice-alien.jpg': 'Alien bridge practice',
    'practice-bridge.jpg': 'Bridge practice',
    'practice-bridge2.jpg': 'Bridge practice variation',
    'practice-cobra.jpg': 'Cobra backbend',
    'practice-cobra2.jpg': 'Cobra backbend 2',
    'practice-cobra3.jpg': 'Cobra backbend 3',
    'practice-knee-bridge.jpg': 'Knee bridge variation',
  },

  // FLAG POSE / PROFILE CATEGORY (4 images)
  flagPoses: {
    'flag-artsy.jpg': 'Artistic flag pose (hanging sideways)',
    'flag-wideshot.jpg': 'Flag pose wide angle',
    'flagpose_square.png': 'Flag pose square format',
    'serious-flag.PNG': 'Serious flag pose',
  },

  // SHOULDER & BALANCE CATEGORY (5 images)
  shoulderBalance: {
    'back-shoulder.jpg': 'Back shoulder pose',
    'band-shoulder-opener.jpg': 'Shoulder opener with band',
    'shoulder-opener.jpg': 'Shoulder opener stretch',
    'bar-balance.jpg': 'Balance on bar',
    'practice-cubes.jpg': 'Practice with props/cubes',
  },
};

/**
 * =================================================================================
 * STRATEGIC MAPPINGS - Assigned to Platform Features
 * =================================================================================
 */

/**
 * Instructor Profile Images
 * Used for: Profile cards, modals, instructor sections
 */
export const instructorProfile = {
  // Primary profile photo - flag poses work great for profile
  primary: getStorageUrl('flagpose_square.png'), // Square format perfect for profile
  
  // Alternative profile photos
  artistic: getStorageUrl('flag-artsy.jpg'), // Artistic shot
  action: getStorageUrl('serious-flag.PNG'), // Serious/professional
  
  // Cover/background images - wide shots
  coverWide: getStorageUrl('flag-wideshot.jpg'),
  coverAction: getStorageUrl('bar-balance.jpg'),
};

/**
 * Class Type Hero Images
 * Mapped to specific class types for visual identity
 */
export const classTypeImages: Record<ClassName, string[]> = {
  'Front Split': [
    getStorageUrl('hanging-split.jpg'), // Primary
    getStorageUrl('practice-side-split.jpg'),
  ],
  
  'Middle Split': [
    getStorageUrl('squat-split.jpg'), // Primary
    getStorageUrl('squatsplit_portrait.png'),
  ],
  
  'Handstands': [
    getStorageUrl('handstand.jpg'), // Primary
    getStorageUrl('practice-handstands.jpg'),
    getStorageUrl('moveom-press.jpg'),
    getStorageUrl('pike-press.jpg'),
    getStorageUrl('press.jpg'),
    getStorageUrl('press-touch.jpg'),
  ],
  
  'Back Flexibility': [
    getStorageUrl('bridge.PNG'), // Primary
    getStorageUrl('alien.jpg'),
    getStorageUrl('alien2.jpg'),
    getStorageUrl('practice-bridge.jpg'),
    getStorageUrl('practice-bridge2.jpg'),
    getStorageUrl('practice-cobra.jpg'),
    getStorageUrl('practice-cobra2.jpg'),
    getStorageUrl('practice-cobra3.jpg'),
    getStorageUrl('practice-alien.jpg'),
    getStorageUrl('practice-knee-bridge.jpg'),
  ],
};

/**
 * Get a random image for a specific class type
 * Adds variety to the UI
 */
export const getClassTypeImage = (className: ClassName): string => {
  const images = classTypeImages[className];
  if (!images || images.length === 0) {
    return instructorProfile.action;
  }
  return images[Math.floor(Math.random() * images.length)];
};

/**
 * Get the primary (first) image for a class type
 * Consistent across sessions
 */
export const getPrimaryClassImage = (className: ClassName): string => {
  const images = classTypeImages[className];
  if (!images || images.length === 0) {
    return instructorProfile.action;
  }
  return images[0];
};

/**
 * Welcome/Hero Banner Images
 * Rotating hero images for welcome sections and page headers
 */
export const heroBannerImages = [
  getStorageUrl('flag-wideshot.jpg'), // Wide hero shot
  getStorageUrl('flag-artsy.jpg'), // Artistic
  getStorageUrl('bar-balance.jpg'), // Action shot
  getStorageUrl('handstand.jpg'), // Impressive skill
];

/**
 * Get a random hero banner image
 */
export const getRandomHeroBanner = (): string => {
  return heroBannerImages[Math.floor(Math.random() * heroBannerImages.length)];
};

/**
 * Success/Celebration Images
 * Used after successful actions (booking, purchase, achievements)
 */
export const celebrationImages = {
  booking: getStorageUrl('practice-handstands.jpg'), // Practice/training vibe
  purchase: getStorageUrl('serious-flag.PNG'), // Achievement unlocked
  achievement: getStorageUrl('handstand.jpg'), // Peak performance
  progress: getStorageUrl('moveom-press.jpg'), // Progress/movement
};

/**
 * Empty State Images
 * Friendly, inviting images for empty states
 */
export const emptyStateImages = {
  noClasses: getStorageUrl('bridge.PNG'), // Back Flexibility class image
  noPackages: getStorageUrl('bar-balance.jpg'), // Balance/preparation
  welcome: getStorageUrl('flagpose_square.png'), // Welcome portrait
  noProgress: getStorageUrl('shoulder-opener.jpg'), // Warm-up/start
};

/**
 * Practice/Training Session Images
 * Various training scenes for backgrounds and content sections
 */
export const trainingImages = [
  getStorageUrl('practice-handstands.jpg'),
  getStorageUrl('practice-side-split.jpg'),
  getStorageUrl('practice-bridge.jpg'),
  getStorageUrl('practice-bridge2.jpg'),
  getStorageUrl('practice-cobra.jpg'),
  getStorageUrl('practice-cobra2.jpg'),
  getStorageUrl('practice-cobra3.jpg'),
  getStorageUrl('practice-alien.jpg'),
  getStorageUrl('practice-knee-bridge.jpg'),
  getStorageUrl('practice-cubes.jpg'),
];

/**
 * Get a random training/practice image
 */
export const getRandomTrainingImage = (): string => {
  return trainingImages[Math.floor(Math.random() * trainingImages.length)];
};

/**
 * Flexibility/Warm-up Images
 * For warm-up sections, flexibility tips, etc.
 */
export const flexibilityImages = {
  shoulderOpener: getStorageUrl('shoulder-opener.jpg'),
  bandOpener: getStorageUrl('band-shoulder-opener.jpg'),
  backShoulder: getStorageUrl('back-shoulder.jpg'),
  kneebridge: getStorageUrl('practice-knee-bridge.jpg'),
};

/**
 * Modal/Detail View Images
 * Larger format images for modal headers and detail views
 */
export const detailViewImages = {
  handstandDetail: getStorageUrl('handstand.jpg'),
  bridgeDetail: getStorageUrl('bridge.PNG'),
  flagDetail: getStorageUrl('serious-flag.PNG'),
  splitDetail: getStorageUrl('hanging-split.jpg'),
};

/**
 * Image categories for easy organization
 * Use this to see all available images by category
 */
export const instructorImages = {
  profile: instructorProfile,
  classTypes: classTypeImages,
  heroBanners: heroBannerImages,
  celebrations: celebrationImages,
  emptyStates: emptyStateImages,
  training: trainingImages,
  flexibility: flexibilityImages,
  detailViews: detailViewImages,
};

/**
 * Default/Fallback Images
 * Used when specific images are not available
 */
export const fallbackImages = {
  instructor: instructorProfile.primary,
  class: instructorProfile.action,
  hero: heroBannerImages[0],
  training: trainingImages[0],
};

/**
 * Helper: Get image by filename
 * For one-off uses or custom implementations
 */
export const getImageByFilename = (filename: string): string => {
  return getStorageUrl(filename);
};

export default instructorImages;

